import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Search, MessageCircle, Mail, Filter, TrendingUp, TrendingDown,
  Wallet, AlertTriangle, CheckCircle2, Clock, FileText, Loader2, X,
  Phone, MapPin, Building2, CalendarDays, Receipt, Sparkles, RefreshCw, ArrowRight,
  CreditCard,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PaymentMethodsModal from '../components/PaymentMethodsModal';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const fmtMoney = (n, cur = 'XOF') => formatCurrency(n || 0, cur);
const fmtNumber = (n) => (Number(n || 0)).toLocaleString('fr-FR');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const STATUS_CONF = {
  a_jour:     { label: 'À jour',       tone: 'emerald', icon: CheckCircle2 },
  avance:     { label: 'En avance',    tone: 'sky',     icon: TrendingUp },
  en_retard:  { label: 'En retard',    tone: 'amber',   icon: Clock },
  impaye:     { label: 'Impayé',       tone: 'rose',    icon: AlertTriangle },
  termine:    { label: 'Bail terminé', tone: 'gray',    icon: FileText },
};

const TONE_CLASSES = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-800', icon: 'text-emerald-600' },
  sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     badge: 'bg-sky-100 text-sky-800',         icon: 'text-sky-600' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   badge: 'bg-amber-100 text-amber-800',     icon: 'text-amber-700' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    badge: 'bg-rose-100 text-rose-800',       icon: 'text-rose-600' },
  gray:    { bg: 'bg-gray-50',    border: 'border-gray-200',    badge: 'bg-gray-100 text-gray-700',       icon: 'text-gray-500' },
};

const SummaryTile = ({ label, value, tone = 'copper', icon: Icon, big }) => {
  const bgs = {
    copper: 'from-[#B85C38] to-[#a04d2f] text-white',
    forest: 'from-[#2B3A2C] to-[#3E4F3F] text-white',
    sand:   'from-white to-[#F5EFE1] text-[#2B3A2C] border border-[#B85C38]/15',
    rose:   'from-rose-500 to-rose-600 text-white',
  };
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br shadow-sm ${bgs[tone]}`}>
      <div className="flex items-center gap-2 opacity-90 mb-1">
        {Icon && <Icon className="w-4 h-4" strokeWidth={1.6} />}
        <p className="text-[11px] uppercase font-semibold tracking-wider font-body-modern">{label}</p>
      </div>
      <p className={`font-display font-bold tracking-tight ${big ? 'text-3xl' : 'text-2xl'}`}>{value}</p>
    </div>
  );
};

const TenantSituationCard = ({ s, onOpen }) => {
  const conf = STATUS_CONF[s.status] || STATUS_CONF.a_jour;
  const tone = TONE_CLASSES[conf.tone];
  const Icon = conf.icon;
  const balanceIsNeg = (s.balance || 0) < 0;

  return (
    <button
      onClick={() => onOpen(s)}
      className={`text-left w-full rounded-2xl border ${tone.border} bg-white p-4 hover:shadow-lg transition ae-hover-lift`}
      data-testid={`tenant-situation-${s.tenant_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${tone.badge}`}>
              <Icon className={`w-4 h-4 ${tone.icon}`} strokeWidth={1.6} />
            </span>
            <div className="min-w-0">
              <p className="font-display font-bold text-sm text-[#2B3A2C] truncate">{s.tenant_name}</p>
              <p className="text-[10px] text-gray-500 font-body-modern truncate">{s.tenant_code || s.tenant_phone || '—'}</p>
            </div>
          </div>
        </div>
        <Badge className={`${tone.badge} border-0 text-[10px] font-body-modern font-semibold`}>{s.status_label}</Badge>
      </div>

      <div className="pb-3 mb-3 border-b border-gray-100 text-xs font-body-modern">
        <p className="text-gray-500 truncate flex items-center gap-1">
          <Building2 className="w-3 h-3" strokeWidth={1.6} /> {s.property_title || '—'}
        </p>
        <p className="text-gray-400 truncate mt-0.5">{s.property_district}</p>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs font-body-modern">
        <div>
          <p className="text-[10px] uppercase text-gray-400 font-semibold">Loyer</p>
          <p className="font-bold text-[#2B3A2C] font-display">{fmtMoney(s.monthly_rent, s.currency)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-gray-400 font-semibold">Solde</p>
          <p className={`font-bold font-display ${balanceIsNeg ? 'text-rose-600' : (s.balance > 0 ? 'text-emerald-600' : 'text-[#2B3A2C]')}`}>
            {balanceIsNeg ? '−' : (s.balance > 0 ? '+' : '')}{fmtMoney(Math.abs(s.balance || 0), s.currency)}
          </p>
        </div>
        <div className="col-span-2 flex items-center justify-between mt-1">
          <span className="text-[10px] text-gray-500">
            {s.last_payment_date ? `Dernier : ${fmtDate(s.last_payment_date)}` : 'Aucun paiement'}
          </span>
          {s.months_behind > 0 && (
            <span className="text-[10px] font-bold text-rose-600">
              {s.days_overdue}j de retard
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const REMINDER_LEVELS = [
  { level: 1, label: '1re relance', desc: 'Rappel amiable et courtois' },
  { level: 2, label: '2e relance', desc: 'Rappel ferme, propose un échéancier' },
  { level: 3, label: 'Dernière relance', desc: 'Ultime rappel avant mise en demeure' },
];

const ReminderLetterDialog = ({ s, open, onClose }) => {
  const [level, setLevel] = useState(1);
  const [grace, setGrace] = useState(8);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    const g = parseInt(grace, 10);
    if (!g || g < 1 || g > 90) { toast.error('Délai de règlement invalide (1 à 90 jours)'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${s.tenant_id}/reminder-letter`,
        { property_id: s.property_id, level, grace_period_days: parseInt(grace || 8, 10) },
        { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      link.download = `relance_${level}_${s.tenant_name}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Courrier de relance téléchargé');
      onClose();
    } catch (e) {
      let msg = 'Erreur lors de la génération';
      try {
        const d = JSON.parse(await e.response.data.text()).detail;
        if (typeof d === 'string') msg = d;
        else if (Array.isArray(d)) msg = 'Délai de règlement invalide (1 à 90 jours)';
      } catch (_) { /* blob non JSON */ }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="reminder-letter-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Courrier de relance</DialogTitle>
          <DialogDescription className="font-body-modern">
            {s.tenant_name} · impayé de {fmtMoney(s.unpaid_amount, s.currency)} détaillé par mois
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {REMINDER_LEVELS.map((l) => (
            <button
              key={l.level}
              type="button"
              onClick={() => setLevel(l.level)}
              className={`w-full text-left rounded-xl border-2 p-3 transition-colors ${level === l.level ? 'border-[#B85C38] bg-[#B85C38]/5' : 'border-gray-200 hover:border-[#B85C38]/40'}`}
              data-testid={`reminder-level-${l.level}`}
            >
              <p className="font-display font-bold text-sm text-[#2B3A2C]">{l.label}</p>
              <p className="text-xs text-gray-500 font-body-modern">{l.desc}</p>
            </button>
          ))}
          <label className="block text-xs font-semibold text-gray-600 font-body-modern pt-2">
            Délai de règlement (jours)
            <input
              type="number" min="1" max="90" value={grace}
              onChange={(e) => setGrace(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-[#B85C38] outline-none"
              data-testid="reminder-grace-input"
            />
          </label>
        </div>
        <Button onClick={generate} disabled={busy} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="reminder-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.6} />}
          Générer le PDF
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const TenantSituationDetail = ({ s, open, onClose }) => {
  const [pmOpen, setPmOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  if (!s) return null;
  const conf = STATUS_CONF[s.status] || STATUS_CONF.a_jour;
  const tone = TONE_CLASSES[conf.tone];
  const balanceIsNeg = (s.balance || 0) < 0;

  const remindWhatsApp = () => {
    const digits = (s.tenant_phone || '').replace(/\D/g, '');
    if (!digits) { toast.error('Aucun numéro'); return; }
    const phone = digits.startsWith('223') ? digits : `223${digits.replace(/^0+/, '')}`;
    const amount = fmtMoney(Math.abs(s.balance || 0), s.currency);
    const text = encodeURIComponent(
      s.months_behind > 0
        ? `Bonjour ${s.tenant_name}, votre solde impayé pour "${s.property_title}" s'élève à ${amount} (${s.months_behind} mois). Merci de régulariser dès que possible.`
        : `Bonjour ${s.tenant_name}, votre situation locative est à jour. Merci pour votre confiance !`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener');
  };
  const remindEmail = () => {
    if (!s.tenant_email) return toast.error('Aucun email');
    window.location.href = `mailto:${s.tenant_email}?subject=${encodeURIComponent('Situation locative')}&body=${encodeURIComponent(`Bonjour ${s.tenant_name},\n\nVotre situation actuelle : ${s.status_label}. Solde : ${fmtMoney(s.balance, s.currency)}.\n\nCordialement.`)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0">
        <div className={`p-5 bg-gradient-to-br ${conf.tone === 'rose' ? 'from-rose-500 to-rose-600' : conf.tone === 'amber' ? 'from-amber-500 to-amber-600' : conf.tone === 'emerald' ? 'from-emerald-500 to-emerald-600' : conf.tone === 'sky' ? 'from-sky-500 to-sky-600' : 'from-[#2B3A2C] to-[#3E4F3F]'} text-white`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <Users className="w-5 h-5" strokeWidth={1.6} /> {s.tenant_name}
            </DialogTitle>
            <DialogDescription className="text-white/85 font-body-modern">
              {s.property_title} · {s.status_label}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5 space-y-4">
          {/* Balance highlight */}
          <div className={`rounded-2xl p-4 border-2 ${tone.border} ${tone.bg}`}>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold font-body-modern">Solde du compte locataire</p>
            <p className={`font-display font-bold text-4xl tracking-tight mt-1 ${balanceIsNeg ? 'text-rose-600' : (s.balance > 0 ? 'text-emerald-700' : 'text-[#2B3A2C]')}`}>
              {balanceIsNeg ? '−' : (s.balance > 0 ? '+' : '')}{fmtMoney(Math.abs(s.balance || 0), s.currency)}
            </p>
            {s.months_behind > 0 ? (
              <p className="text-xs text-rose-700 mt-1 font-body-modern">
                {s.months_behind} mois de loyer en retard · {s.days_overdue} jour(s) depuis le plus ancien impayé
              </p>
            ) : (
              <p className="text-xs text-gray-600 mt-1 font-body-modern">Situation à jour au {fmtDate(new Date().toISOString())}</p>
            )}
          </div>

          {/* Key figures */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="rounded-xl bg-white border border-[#B85C38]/15 p-3">
              <p className="text-[10px] text-gray-500 uppercase font-body-modern">Loyer mensuel</p>
              <p className="font-display font-bold text-[#2B3A2C]">{fmtMoney(s.monthly_rent, s.currency)}</p>
            </div>
            <div className="rounded-xl bg-white border border-[#B85C38]/15 p-3">
              <p className="text-[10px] text-gray-500 uppercase font-body-modern">Cumul attendu</p>
              <p className="font-display font-bold text-[#2B3A2C]">{fmtMoney(s.total_expected, s.currency)}</p>
              <p className="text-[9px] text-gray-400 font-body-modern">sur {s.months_expected} mois</p>
            </div>
            <div className="rounded-xl bg-white border border-[#B85C38]/15 p-3">
              <p className="text-[10px] text-gray-500 uppercase font-body-modern">Cumul encaissé</p>
              <p className="font-display font-bold text-emerald-700">{fmtMoney(s.total_paid, s.currency)}</p>
              <p className="text-[9px] text-gray-400 font-body-modern">{s.payments_count} paiement(s)</p>
            </div>
            <div className="rounded-xl bg-white border border-[#B85C38]/15 p-3">
              <p className="text-[10px] text-gray-500 uppercase font-body-modern">Dépôt de garantie</p>
              <p className="font-display font-bold text-[#B85C38]">{s.deposit_amount ? fmtMoney(s.deposit_amount, s.currency) : '—'}</p>
              <p className="text-[9px] text-gray-400 font-body-modern">{s.deposit_reference || 'Aucun'}</p>
            </div>
          </div>

          {/* Contact + Lease info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#F5EFE1]/60 border border-[#B85C38]/15 p-3 space-y-2">
              <p className="text-[10px] uppercase text-gray-500 font-semibold font-body-modern">Contact locataire</p>
              <div className="text-sm text-[#2B3A2C] font-body-modern space-y-1">
                <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.6} /> {s.tenant_phone || '—'}</p>
                <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.6} /> {s.tenant_email || '—'}</p>
              </div>
            </div>
            <div className="rounded-xl bg-[#F5EFE1]/60 border border-[#B85C38]/15 p-3 space-y-2">
              <p className="text-[10px] uppercase text-gray-500 font-semibold font-body-modern">Bail</p>
              <div className="text-sm text-[#2B3A2C] font-body-modern space-y-1">
                <p className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.6} /> Début : {fmtDate(s.lease_start_date)}</p>
                <p className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.6} /> Fin : {fmtDate(s.lease_end_date)}</p>
                <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.6} /> Prochaine échéance : {fmtDate(s.next_due_date)}</p>
              </div>
            </div>
          </div>

          {/* Last payment */}
          {s.last_payment_date && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-3">
              <Receipt className="w-5 h-5 text-emerald-600" strokeWidth={1.6} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 font-body-modern">Dernier paiement</p>
                <p className="font-body-modern text-sm">
                  <span className="font-bold text-[#2B3A2C]">{fmtMoney(s.last_payment_amount, s.currency)}</span>
                  <span className="text-gray-500"> · {fmtDate(s.last_payment_date)}</span>
                  {s.last_receipt_number && <span className="text-gray-500"> · {s.last_receipt_number}</span>}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={remindWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="ts-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-1.5" strokeWidth={1.6} /> Relance WhatsApp
            </Button>
            {s.unpaid_amount > 0 && (
              <Button onClick={() => setReminderOpen(true)} className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="ts-reminder-letter-btn">
                <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.6} /> Courrier de relance
              </Button>
            )}
            <Button onClick={remindEmail} variant="outline" className="border-sky-400 text-sky-700 hover:bg-sky-50 rounded-xl" data-testid="ts-email-btn">
              <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.6} /> Email
            </Button>
            <Button onClick={() => setPmOpen(true)} className="bg-[#2B3A2C] hover:bg-[#3E4F3F] text-white rounded-xl" data-testid="ts-payment-methods-btn">
              <CreditCard className="w-4 h-4 mr-1.5" strokeWidth={1.6} /> Modes de paiement
            </Button>
            <Link to={`/rent-payments?tenant=${s.tenant_id}&property=${s.property_id}`} className="inline-flex items-center gap-1.5 rounded-xl border border-[#B85C38]/30 text-[#B85C38] hover:bg-[#B85C38]/10 px-3 py-2 text-sm font-medium">
              <Receipt className="w-4 h-4" strokeWidth={1.6} /> Enregistrer paiement
            </Link>
            <Link to={`/clients?id=${s.tenant_id}`} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-2 text-sm font-medium">
              <Users className="w-4 h-4" strokeWidth={1.6} /> Fiche complète <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </DialogContent>
      <PaymentMethodsModal
        open={pmOpen}
        onClose={() => setPmOpen(false)}
        transactionId={s.transaction_id}
        tenantName={s.tenant_name}
      />
      <ReminderLetterDialog s={s} open={reminderOpen} onClose={() => setReminderOpen(false)} />
    </Dialog>
  );
};

const TenantSituations = () => {
  const [data, setData] = useState({ items: [], summary: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tenant-situations');
      setData(r.data || { items: [], summary: null });
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let items = data.items || [];
    if (filter !== 'all') items = items.filter((s) => s.status === filter);
    const q = search.trim().toLowerCase();
    if (q) items = items.filter((s) =>
      (s.tenant_name || '').toLowerCase().includes(q) ||
      (s.property_title || '').toLowerCase().includes(q) ||
      (s.tenant_phone || '').includes(q)
    );
    return items;
  }, [data.items, filter, search]);

  const openDetail = (s) => { setSelected(s); setDetailOpen(true); };

  const summary = data.summary || { total_expected: 0, total_paid: 0, total_outstanding: 0, by_status: {} };
  const collectionRate = summary.total_expected > 0
    ? (summary.total_paid / summary.total_expected) * 100
    : 0;

  return (
    <div className="space-y-6 pb-10" data-testid="tenant-situations-page">
      <div>
        <p className="text-[11px] uppercase tracking-widest font-semibold text-[#B85C38] mb-2 font-body-modern flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.6} /> Vue synthétique
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#2B3A2C] tracking-tight">Situation des locataires</h1>
        <p className="text-sm text-gray-500 mt-1 font-body-modern">
          Solde, retards, historique et actions rapides pour chaque locataire actif.
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label="Attendu (cumul)" value={fmtMoney(summary.total_expected)} tone="forest" icon={Wallet} big />
        <SummaryTile label="Encaissé (cumul)" value={fmtMoney(summary.total_paid)} tone="sand" icon={CheckCircle2} big />
        <SummaryTile
          label="Reste dû global"
          value={fmtMoney(summary.total_outstanding)}
          tone={summary.total_outstanding > 0 ? 'copper' : 'sand'}
          icon={AlertTriangle}
          big
        />
        <SummaryTile
          label="Taux d'encaissement"
          value={`${collectionRate.toFixed(1)}%`}
          tone={collectionRate >= 75 ? 'forest' : collectionRate >= 50 ? 'copper' : 'rose'}
          icon={TrendingUp}
          big
        />
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un locataire, bien ou téléphone…"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-[#F5EFE1] focus:bg-white focus:ring-2 focus:ring-[#B85C38] outline-none border border-transparent font-body-modern text-sm"
            data-testid="ts-search"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { key: 'all',       label: 'Tous',     count: data.count },
            { key: 'impaye',    label: 'Impayés',  count: summary.by_status?.impaye || 0 },
            { key: 'en_retard', label: 'Retards',  count: summary.by_status?.en_retard || 0 },
            { key: 'a_jour',    label: 'À jour',   count: summary.by_status?.a_jour || 0 },
            { key: 'avance',    label: 'Avances',  count: summary.by_status?.avance || 0 },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-semibold font-body-modern px-3 py-1.5 rounded-full transition ${
                filter === f.key
                  ? 'bg-[#B85C38] text-white shadow'
                  : 'bg-[#F5EFE1] text-gray-700 hover:bg-[#F5EFE1]/70'
              }`}
              data-testid={`ts-filter-${f.key}`}
            >
              {f.label} <span className="opacity-75">({f.count})</span>
            </button>
          ))}
        </div>
        <Button onClick={fetchData} variant="outline" className="rounded-xl border-[#B85C38]/30 text-[#B85C38] hover:bg-[#B85C38]/10" data-testid="ts-refresh-btn">
          <RefreshCw className="w-4 h-4" strokeWidth={1.6} />
        </Button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-10 text-center">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" strokeWidth={1.4} />
          <p className="font-display font-bold text-lg text-[#2B3A2C]">Aucun locataire trouvé</p>
          <p className="text-xs text-gray-500 mt-1 font-body-modern">Ajustez vos filtres ou votre recherche.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <TenantSituationCard key={`${s.tenant_id}-${s.transaction_id}`} s={s} onOpen={openDetail} />
          ))}
        </div>
      )}

      <TenantSituationDetail s={selected} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </div>
  );
};

export default TenantSituations;
